import { Box, Card, Divider, Flex, Group, ScrollArea, Skeleton, Stack, Tooltip } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { RevisionListResponse } from 'api/actions/revision-list/revision-list-response';
import { useApi } from 'api/api-context';
import DeviceList from 'components/DeviceList';
import NumberBadge from 'components/NumberBadge';
import RevisionStatusIcon from 'components/RevisionStatusIcon';
import DeviceTypeLabel from 'components/device/DeviceTypeLabel';
import RevisionActions from 'components/tables/data/revision/RevisionActions';
import H3Medium from 'components/typography/H3Medium';
import P3Regular from 'components/typography/P3Regular';
import panic from 'errors/panic';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FORMS_PAGE_PATH, REVISIONS_PAGE_PATH } from 'routes/paths';
import Button from 'components/Button';
import { getEnabledDocumentKindFilter } from 'features/organization-modules/use-organization-modules';

/**
 * Parameters of the RevisionsTableCard component.
 */
export interface RevisionsTableCardProps {
  limit?: number;
  enabledKinds?: ('revision' | 'form')[];
  onDataChange?: () => void;
}

/**
 * Displays a simplified table of revisions.
 */
export default function RevisionsTableCard({
  limit = 10,
  enabledKinds = ['revision', 'form'],
  onDataChange,
}: RevisionsTableCardProps = {}) {
  const { getAction, hasPermissionAnywhere } = useApi();
  const { isMobile } = useResponsiveDesign();
  const [revisions, setRevisions] = useState<RevisionListResponse>([]);
  const [loading, setLoading] = useState(true);
  const revisionsEnabled = enabledKinds.includes('revision');
  const formsEnabled = enabledKinds.includes('form');
  const someRevisions = useMemo(() => revisions.slice(0, limit), [revisions]);

  const moreLinkRevisions = useMemo(() => `${REVISIONS_PAGE_PATH.original}?revisionStatusId=1,2,3,4,5,6,7`, []);
  const moreLinkForms = useMemo(() => `${FORMS_PAGE_PATH.original}?revisionStatusId=1,2,3,4,5,6,7`, []);

  const canDealWithRevisions = hasPermissionAnywhere('*-revisions');
  const canDealWithForms = hasPermissionAnywhere('*-forms');

  const nRevisions = useMemo(() => revisions.filter(({ kind }) => kind === 'revision').length, [revisions]);
  const nForms = useMemo(() => revisions.length - nRevisions, [revisions, nRevisions]);

  /**
   * Updates the revision with the given ID.
   */
  const updateRevision = useCallback(
    (revisionId: number, update: Partial<RevisionListResponse[number]>) =>
      setRevisions((revisions) =>
        revisions.map((revision) => (revision.revisionId === revisionId ? { ...revision, ...update } : revision))
      ),
    [setRevisions]
  );

  /**
   * Removes a revision from the list when the user no longer has access to it.
   */
  const removeRevision = useCallback(
    (revisionId: number) => {
      setRevisions((revisions) => revisions.filter((revision) => revision.revisionId !== revisionId));
      onDataChange?.();
    },
    [setRevisions, onDataChange]
  );

  useEffect(() => {
    const revisionListAction = getAction('RevisionList');

    const filters: Record<string, unknown> = {
      'statusId.in': [1, 2, 3, 4, 5, 6, 7],
      'includedInStatistics.eq': 1,
    };
    Object.assign(filters, getEnabledDocumentKindFilter(revisionsEnabled, formsEnabled));

    revisionListAction({ query: { filters } })
      .then((revisions) =>
        setRevisions(
          Array.isArray(revisions)
            ? revisions.filter(({ kind }) => (kind === 'revision' ? revisionsEnabled : formsEnabled))
            : []
        )
      )
      .catch(panic)
      .finally(() => setLoading(false));
  }, [formsEnabled, getAction, revisionsEnabled]);

  if (loading) {
    return <Skeleton radius={4} w="100%" mih={400} />;
  }

  return (
    <Card p={24} h="fit-content">
      <Group spacing={4} pb={24}>
        <H3Medium color="gray.8">Čakajúce na akciu</H3Medium>
        <Box sx={{ transform: 'scale(0.66)' }}>
          <NumberBadge variant="gray" value={revisions.length} />
        </Box>
      </Group>
      <ScrollArea h={416} mr={-20} pr={20}>
        {someRevisions.map((revision) => (
          <Stack key={revision.revisionId} spacing={0}>
            <Divider color="gray.1" />
            <Group noWrap mih={82} spacing={16} pt={12} pb={16} position="apart">
              <Stack spacing={4}>
                <Group spacing={12}>
                  <Group mr={12} noWrap spacing={4}>
                    <Tooltip label={revision.deviceType.deviceTypeName}>
                      <Box>
                        <DeviceTypeLabel iconOnly deviceType={revision.deviceType.deviceTypeId} />
                      </Box>
                    </Tooltip>
                    <DeviceList devices={revision.devices} limit={1} />
                  </Group>
                  <RevisionStatusIcon statusId={revision.revisionStatus.revisionStatusId} kind={revision.kind} />
                </Group>
                <P3Regular color="gray.7">
                  {revision.revisionName} • {revision.organization.organizationName} (
                  {revision.department.departmentName})
                </P3Regular>
              </Stack>
              <RevisionActions
                revision={revision}
                compactOnTablet={isMobile}
                keepPrimaryActionOnMobile
                onStatusChange={({ status, technician }) =>
                  updateRevision(revision.revisionId, {
                    revisionStatus: status,
                    technician: technician ?? revision.technician,
                  })
                }
                onNoLongerAccessible={() => removeRevision(revision.revisionId)}
              />
            </Group>
          </Stack>
        ))}
      </ScrollArea>
      <Divider color="gray.1" />
      <Box pt={16} sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <Flex align="center" justify="space-between" gap={16} miw="100%" w="max-content">
          <Group noWrap spacing={8}>
            {canDealWithForms && nForms > 0 && (
              <Button
                component={Link}
                to={moreLinkForms}
                variant="subtle"
                size="sm"
                rightIcon={<IconChevronRight size={16} />}
              >
                Všetky čakajúce formuláre ({nForms})
              </Button>
            )}
            {canDealWithRevisions && nRevisions > 0 && (
              <Button
                component={Link}
                to={moreLinkRevisions}
                variant="subtle"
                size="sm"
                rightIcon={<IconChevronRight size={16} />}
              >
                Všetky čakajúce revízie ({nRevisions})
              </Button>
            )}
          </Group>
          <P3Regular color="gray.7">
            Zobrazené: {someRevisions.length} / {revisions.length}
          </P3Regular>
        </Flex>
      </Box>
    </Card>
  );
}
